import Image from "next/image";

import { cn } from "@/lib/utils";

export interface CoreValue {
  title: string;
  description: string;
}

interface FoundersSectionContent {
  badge: string;
  heading: string;
  foundersImage: string;
  foundersLabel: string;
  founders: string[];
}

interface FoundersSectionProps {
  values: CoreValue[];
  content: FoundersSectionContent;
}

export default function FoundersSection({ values, content }: FoundersSectionProps) {
  return (
    <section className="bg-clay-100 flex flex-col items-center gap-8 px-5 py-12 lg:px-8 lg:py-28 w-full">
      <div className="flex flex-col items-center gap-3 lg:gap-6 text-center">
        <span className="text-rebolt-500 text-xs font-bold uppercase tracking-[1px]">{content.badge}</span>
        <h2 className="font-clash font-medium text-4xl lg:text-6xl leading-[1.1] lg:leading-none tracking-[-0.64px] lg:tracking-[-1.2px] text-ash-900">
          {content.heading}
        </h2>
      </div>

      <div className="bg-white border border-ash-300 rounded-3xl px-5 py-8 lg:px-8 lg:py-16 flex flex-col lg:flex-row gap-8 lg:gap-10 w-full max-w-5xl">
        {/* values */}
        <div className="flex flex-col flex-1 order-1 lg:order-2 lg:px-6">
          {values.map((value, i) => (
            <div
              key={value.title}
              className={cn("flex flex-col gap-3 pt-5 pb-6", i < values.length - 1 && "border-b border-ash-300")}
            >
              <h3 className="font-bold text-lg leading-normal text-black">{value.title}</h3>
              <p className="text-sm leading-normal text-ash-500">{value.description}</p>
            </div>
          ))}
        </div>

        {/* founders */}
        <div className="flex flex-row lg:flex-col gap-6 items-center lg:items-end order-2 lg:order-1 lg:w-72 lg:justify-center pt-6 lg:pt-0">
          <div className="relative lg:w-full lg:h-50 md:w-56 md:h-34 w-47 h-34 rounded-xl lg:rounded-2xl overflow-hidden shrink-0">
            <Image
              src={content.foundersImage}
              alt={content.foundersLabel}
              fill
              className="object-cover w-full h-full grayscale"
            />
          </div>
          <div className="flex flex-col gap-0.75 lg:gap-2 lg:items-end lg:text-right">
            <span className="text-xs sm:text-sm text-ash-500 leading-normal">{content.foundersLabel}</span>
            <div className="flex flex-col lg:items-end">
              {content.founders.map((name, i) => (
                <span key={name} className="contents">
                  {i > 0 && <span className="text-xs sm:text-sm text-ash-500">and</span>}
                  <span className={cn("text-4xl lg:text-6xl text-black font-belle-aurore", i > 0 && "md:mt-4 mt-3")}>
                    {name}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
